import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useArePeopleVisible } from "@/hooks/useArePeopleVisible";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { AssignationsDTO, UserListingDTO } from "@shaarp/common";
import {
  Building2Icon,
  ChevronDownIcon,
  ChevronRightIcon,
  ListIcon,
  UserPlusIcon,
  UsersIcon,
} from "lucide-react";
import { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

type AssignedCategoriesListProps = {
  assignations: AssignationsDTO;
  assignedListings: UserListingDTO[];
  listingId: string | undefined;
};

export default function AssignedCategoriesList({
  assignations,
  assignedListings,
  listingId,
}: AssignedCategoriesListProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  // A team whose contact switch is off sees no key contacts (#757).
  const arePeopleVisible = useArePeopleVisible();

  const isCompaniesPage = location.pathname.includes(
    "/listing/assignations/companies",
  );
  const isEmployeesPage = location.pathname.includes(
    "/listing/assignations/employees",
  );
  const isListingPage = !isCompaniesPage && !isEmployeesPage;
  const isListsRootSelected = isListingPage && !listingId;

  const [listsOpen, setListsOpen] = useState<boolean>(isListingPage);

  return (
    <div className="space-y-4">
      <div className="p-3 rounded-lg border bg-majorelle/5 border-majorelle/20">
        <div className="flex gap-2 items-center mb-1">
          <Building2Icon className="w-4 h-4 shrink-0 text-majorelle" />
          <span className="text-sm font-semibold">
            {t("listing.page.assigned.managerCard.title")}
          </span>
        </div>
        <p className="text-xs text-muted-foreground">
          {t("listing.page.assigned.managerCard.description")}
        </p>
      </div>

      <div className="space-y-2">
        <div className="px-1 text-xs font-bold tracking-wide uppercase text-majorelle">
          {t("listing.page.assigned.categories.title")}
        </div>

        <NavLink
          to="/listing/assignations/companies"
          className={cn(
            "flex justify-between items-center px-[7px] py-[7px] text-sm rounded-lg border-2 border-transparent bg-background transition-all hover:bg-accent",
            isCompaniesPage && "border-majorelle",
          )}
        >
          <span className="flex flex-1 gap-2 items-center min-w-0">
            <span className="flex justify-center items-center w-7 h-7 rounded-md shrink-0 bg-muted/70">
              <Building2Icon className="w-4 h-4 text-muted-foreground" />
            </span>
            <span className="font-semibold truncate">
              {t("listing.page.assigned.categories.companies")}
            </span>
          </span>
          <Badge
            className={cn(
              "ml-2 px-2 py-0.5 text-xs font-medium rounded-full border-transparent",
              isCompaniesPage
                ? "bg-majorelle text-white hover:bg-majorelle"
                : "bg-zinc-200 text-zinc-700 dark:bg-zinc-700 dark:text-zinc-200 hover:bg-zinc-200 dark:hover:bg-zinc-700",
            )}
          >
            {assignations.companiesCount}
          </Badge>
        </NavLink>

        {arePeopleVisible && (
          <NavLink
            to="/listing/assignations/employees"
            className={cn(
              "flex justify-between items-center px-[7px] py-[7px] text-sm rounded-lg border-2 border-transparent bg-background transition-all hover:bg-accent",
              isEmployeesPage && "border-majorelle",
            )}
          >
            <span className="flex flex-1 gap-2 items-center min-w-0">
              <span className="flex justify-center items-center w-7 h-7 rounded-md shrink-0 bg-muted/70">
                <UserPlusIcon className="w-4 h-4 text-muted-foreground" />
              </span>
              <span className="font-semibold truncate">
                {t("listing.page.assigned.categories.contacts")}
              </span>
            </span>
            <Badge
              className={cn(
                "ml-2 px-2 py-0.5 text-xs font-medium rounded-full border-transparent",
                isEmployeesPage
                  ? "bg-majorelle text-white hover:bg-majorelle"
                  : "bg-zinc-200 text-zinc-700 dark:bg-zinc-700 dark:text-zinc-200 hover:bg-zinc-200 dark:hover:bg-zinc-700",
              )}
            >
              {assignations.employeesCount}
            </Badge>
          </NavLink>
        )}

        <Collapsible open={listsOpen} onOpenChange={setListsOpen}>
          <CollapsibleTrigger asChild>
            <button
              type="button"
              onClick={() => {
                if (!isListsRootSelected) {
                  navigate("/listing");
                }
              }}
              className={cn(
                "flex justify-between items-center px-[7px] py-[7px] w-full text-sm rounded-lg border-2 border-transparent bg-background transition-all hover:bg-accent",
                isListsRootSelected && "border-majorelle",
              )}
            >
              <span className="flex flex-1 gap-2 items-center min-w-0">
                <span className="flex justify-center items-center w-7 h-7 rounded-md shrink-0 bg-muted/70">
                  <ListIcon className="w-4 h-4 text-muted-foreground" />
                </span>
                <span className="font-semibold truncate">
                  {t("listing.page.assigned.categories.lists")}
                </span>
              </span>
              <span className="flex gap-1 items-center ml-2">
                <Badge
                  className={cn(
                    "px-2 py-0.5 text-xs font-medium rounded-full border-transparent",
                    isListsRootSelected
                      ? "bg-majorelle text-white hover:bg-majorelle"
                      : "bg-zinc-200 text-zinc-700 dark:bg-zinc-700 dark:text-zinc-200 hover:bg-zinc-200 dark:hover:bg-zinc-700",
                  )}
                >
                  {assignedListings.length}
                </Badge>
                {listsOpen ? (
                  <ChevronDownIcon className="w-4 h-4 text-muted-foreground" />
                ) : (
                  <ChevronRightIcon className="w-4 h-4 text-muted-foreground" />
                )}
              </span>
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            {assignedListings.length === 0 ? (
              <p className="px-3 py-2 text-xs text-muted-foreground">
                {t("listing.page.assigned.categories.listsEmpty")}
              </p>
            ) : (
              <div className="flex relative flex-col gap-2 pt-2 pl-6">
                <span className="absolute top-2 bottom-2 left-4 w-0.5 rounded-full bg-border" />
                {assignedListings.map((listing) => {
                  const isSelected = listingId === listing.id;
                  const count =
                    listing.listingType === "employees"
                      ? listing.employeesCount
                      : listing.companiesCount;
                  return (
                    <NavLink
                      key={listing.id}
                      to={`/listing/${listing.id}`}
                      className={cn(
                        "flex justify-between items-center gap-2 px-[7px] py-[7px] text-sm rounded-lg border-2 border-transparent bg-background transition-all hover:bg-accent",
                        isSelected && "border-majorelle",
                      )}
                    >
                      <span className="flex flex-1 gap-2 items-center min-w-0">
                        <span className="flex justify-center items-center w-7 h-7 rounded-md shrink-0 bg-muted/70">
                          {listing.listingType === "employees" ? (
                            <UsersIcon className="w-4 h-4 text-muted-foreground" />
                          ) : (
                            <Building2Icon className="w-4 h-4 text-muted-foreground" />
                          )}
                        </span>
                        <span className="font-semibold truncate">
                          {listing.title}
                        </span>
                      </span>
                      <span className="flex gap-2 items-center shrink-0">
                        <Badge
                          className={cn(
                            "px-2 py-0.5 text-xs font-medium rounded-full border-transparent",
                            isSelected
                              ? "bg-majorelle text-white hover:bg-majorelle"
                              : "bg-zinc-200 text-zinc-700 dark:bg-zinc-700 dark:text-zinc-200 hover:bg-zinc-200 dark:hover:bg-zinc-700",
                          )}
                        >
                          {count}
                        </Badge>
                        {listing.author && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Avatar className="w-6 h-6">
                                <AvatarImage src={listing.author.imageUrl} />
                                <AvatarFallback className="text-[10px] font-semibold">
                                  {listing.author.firstName?.charAt(0)}
                                  {listing.author.lastName?.charAt(0)}
                                </AvatarFallback>
                              </Avatar>
                            </TooltipTrigger>
                            <TooltipContent>
                              {listing.author.fullName}
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </span>
                    </NavLink>
                  );
                })}
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>
      </div>
    </div>
  );
}
